<template>
  <div class="ca-file-upload">
    <uoload
      ref="uploader"
      upload-title="上传"
      :file-id="fileId"
      :filetypes="filetypes"
      :max-size="maxSizeBytes"
      :accept="accept"
      :show-file-list="false"
      @fileIdCb="handleFileIdCb"
      @uploadStart="handleUploadStart"
      @uploadProgress="handleUploadProgress"
      @uploadError="handleUploadError"
    >
      <!-- 触发器可通过 trigger 插槽自定义（如电汇页的文字「上传」按钮）；不传则用默认图标方块 -->
      <template v-if="$slots.trigger" slot="trigger">
        <slot name="trigger" />
      </template>
      <button
        v-else
        slot="trigger"
        type="button"
        class="ca-file-upload__trigger"
        :class="{ 'is-uploading': uploading }"
        :disabled="uploading"
      >
        <i :class="uploading ? 'el-icon-loading' : 'el-icon-upload2'" />
      </button>
      <!-- 提示说明：hideExplain 时不渲染（如 CA申请授权书字段，原型无格式限制说明） -->
      <span v-if="!hideExplain" slot="explain">{{ explainText }}</span>
    </uoload>
    <div v-if="uploading" class="ca-file-upload__progress">
      <div class="ca-file-upload__progress-track">
        <div class="ca-file-upload__progress-bar" :style="{ width: uploadPercent + '%' }"></div>
      </div>
      <span class="ca-file-upload__progress-text">上传中 {{ uploadPercent }}%</span>
    </div>
    <div v-if="fileId && fileName" class="ca-file-upload__result">
      <span class="ca-file-upload__name" :title="fileName">{{ fileName }}</span>
      <i class="el-icon-view ca-file-upload__icon" title="预览" @click="handlePreview" />
      <i class="el-icon-delete ca-file-upload__icon" title="删除" @click="handleRemove" />
    </div>
  </div>
</template>

<script>
import Uoload from '@/common/upload/uoload.vue'
import { queryByFileId } from '@/api/upload'
import { FILE_MAX_SIZE } from '../util/caForm'

/**
 * CA 表单内附件上传项（申请页 / 订单编辑页 / 电汇页共用）。
 * 只认 fileId，不感知业务字段与 CA 类型 —— 字段 label、必填星号、校验规则都留在
 * 使用方的 el-form-item 层（util/caForm.js 统一定义）。
 * 上传成功后不用 el-upload 自带的文件列表，而是自渲染「文件名 + 预览 / 删除」。
 *
 * 与底座 uoload.vue 对接的两个易错点：
 *  - maxSize 单位是字节（底座内部再换算成 MB 展示），而 FILE_MAX_SIZE 是 MB，
 *    所以传参必须 ×1024×1024；
 *  - filetypes 必须显式传 ['jpg','jpeg','png'] 覆盖底座默认值（默认含 pdf/word/rar 等，
 *    CA 业务只允许图片）。
 */
export default {
  name: 'FileUpload',
  components: { Uoload },
  props: {
    fileId: { type: String, default: '' }, // 已上传文件的 fileId；空串 = 未上传
    // 删除操作的形态：true = 红色文字「删除」（电汇页原型如此）；false = 删除图标（默认）
    deleteAsText: { type: Boolean, default: false },
    // 上传按钮下方的提示文案（按原型逐字段配置，如营业执照扫描件需公章）；空串走默认通用提示
    explain: { type: String, default: '' },
    // true = 完全不展示文件类型/大小限制说明（如 CA申请授权书，原型该字段无说明行）
    hideExplain: { type: Boolean, default: false },
    // 可上传文件类型白名单（传给底座 uoload 校验）；原生选择框的 accept 过滤由它自动推导，
    // 无需再单独传（doc 类字段如 CA申请授权书传 ['doc', 'docx'] 即可）
    filetypes: { type: Array, default: () => ['jpg', 'jpeg', 'png', 'pdf'] },
    // 单文件大小上限（MB），默认取 CA 模块统一常量 FILE_MAX_SIZE(20)；特殊字段可传入覆盖
    maxSize: { type: Number, default: FILE_MAX_SIZE },
  },
  data() {
    return {
      fileName: '', // fileId 对应的原始文件名（queryByFileId 接口回显）
      uploading: false, // 是否正在上传（大文件上传耗时较长，期间按钮转圈 + 进度条反馈）
      uploadPercent: 0, // 上传进度百分比（0-100，element-ui on-progress 真实进度）
    }
  },
  computed: {
    // 原生选择框的 accept 过滤，由 filetypes 推导（'.jpg,.jpeg,...'）
    accept() {
      return this.filetypes.map((t) => '.' + t).join(',')
    },
    maxSizeMB() {
      return this.maxSize
    },
    maxSizeBytes() {
      return this.maxSize * 1024 * 1024
    },
    // 提示文案：字段配置了专属提示（explain）就用专属，否则按 filetypes 自动拼出格式说明
    explainText() {
      // ，大小不超过 ${this.maxSizeMB}MB
      return this.explain || `仅支持 ${this.filetypes.join(' / ')} 格式`
    },
  },
  watch: {
    fileId: {
      immediate: true,
      handler(id) {
        if (!id) {
          this.fileName = ''
          return
        }
        queryByFileId({ fileId: id }).then((res) => {
          this.fileName = (res.data && res.data.fileName) || ''
        })
      },
    },
  },
  methods: {
    handleFileIdCb(res) {
      this.uploading = false
      this.uploadPercent = 0
      this.$emit('fileIdCb', { url: (res && res.url) || '' })
    },
    handleUploadStart() {
      this.uploading = true
      this.uploadPercent = 0
    },
    handleUploadProgress(percent) {
      // on-progress 是「请求体已发出」的进度，先于后端处理完成返回，到 100 时往往还要再等几秒。
      // 展示上限锁 99，避免出现「已 100% 却还没出来」的假完成；真正完成以 fileIdCb(上传成功) 为准。
      this.uploadPercent = Math.min(99, percent)
    },
    handleUploadError() {
      this.uploading = false
      this.uploadPercent = 0
    },
    handlePreview() {
      if (this.fileId) window.open('/zcjf/base/file/download/' + this.fileId)
    },
    handleRemove() {
      this.$refs.uploader.clearFiles()
      this.$emit('fileIdCb', { url: '' })
    },
  },
}
</script>
